import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DIRECTIONS, LIST_TABS, PAGE_SIZE, SORT_OPTIONS } from '../../constants/shipments';

// The list filters live in the URL (?tab=&status=a,b&carrier=&direction=&customerId=&supplierId=&from=&to=&q=
// &highlight=&sort=&order=&page=), so a list can be bookmarked and the back button restores it.

const HIGHLIGHTS = ['delayed', 'problem', 'stuck', 'awaitingTracking'];
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const ARRAY_KEYS = ['status', 'carrier'];

const csv = (value) => (value ? value.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 20) : []);
const positiveInt = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : '';
};

export function parseFilters(params) {
  const pick = (key, allowed) => (allowed.includes(params.get(key)) ? params.get(key) : '');
  const tab = pick('tab', LIST_TABS.map((t) => t.value));
  const from = params.get('from') || '';
  const to = params.get('to') || '';
  return {
    tab: tab || 'active',
    status: csv(params.get('status')),
    carrier: csv(params.get('carrier')),
    direction: pick('direction', DIRECTIONS.map((d) => d.value)),
    customerId: positiveInt(params.get('customerId')),
    supplierId: positiveInt(params.get('supplierId')),
    from: DATE.test(from) ? from : '',
    to: DATE.test(to) ? to : '',
    q: (params.get('q') || '').slice(0, 100),
    highlight: pick('highlight', HIGHLIGHTS),
    sort: pick('sort', SORT_OPTIONS.map((s) => s.value)),
    order: pick('order', ['asc', 'desc']),
    page: positiveInt(params.get('page')) || 1,
  };
}

// Query for GET /api/shipments/
export function filtersToQuery(f) {
  return {
    tab: f.tab, status: f.status, carrier: f.carrier, direction: f.direction, customerId: f.customerId,
    supplierId: f.supplierId, from: f.from, to: f.to, q: f.q, highlight: f.highlight, sort: f.sort, order: f.order,
    limit: PAGE_SIZE, offset: (f.page - 1) * PAGE_SIZE,
  };
}

// Number of narrowing filters (tab, sorting and paging are not counted)
export function activeFilterCount(f) {
  return [f.status.length, f.carrier.length, f.direction, f.customerId, f.supplierId, f.from, f.to, f.q, f.highlight]
    .filter(Boolean).length;
}

// Writes a patch into URLSearchParams: empty values are removed, the default tab is omitted, paging restarts
export function applyPatch(base, patch) {
  const next = new URLSearchParams(base);
  Object.entries(patch).forEach(([key, value]) => {
    const text = ARRAY_KEYS.includes(key) && Array.isArray(value) ? value.join(',') : value;
    if (text === '' || text == null || (key === 'tab' && text === 'active') || (key === 'page' && Number(text) <= 1)) next.delete(key);
    else next.set(key, String(text));
  });
  if (!('page' in patch)) next.delete('page');
  return next;
}

// "/zasilky?tab=active&highlight=delayed" for links from other pages (dashboard)
export function buildListUrl(patch = {}) {
  const text = applyPatch(new URLSearchParams(), patch).toString();
  return text ? `/zasilky?${text}` : '/zasilky';
}

export function useShipmentFilters() {
  const [params, setParams] = useSearchParams();
  const latest = useRef({ params, setParams });
  useEffect(() => {
    latest.current = { params, setParams };
  });

  const filters = useMemo(() => parseFilters(params), [params]);

  // Stable: reads the newest params, so a delayed (debounced) write does not overwrite a newer URL.
  // `patch` may be a function (currentFilters) => patch, for changes that depend on the current value.
  const setFilters = useCallback((patch, options) => {
    const { params: current, setParams: write } = latest.current;
    const resolved = typeof patch === 'function' ? patch(parseFilters(current)) : patch;
    const next = applyPatch(current, resolved);
    latest.current = { params: next, setParams: write }; // a second call before the re-render builds on this one
    write(next, options);
  }, []);

  const clearFilters = useCallback(() => {
    const { params: current, setParams: write } = latest.current;
    const keep = new URLSearchParams();
    ['tab', 'sort', 'order'].forEach((k) => current.get(k) && keep.set(k, current.get(k)));
    write(keep);
  }, []);

  return { filters, setFilters, clearFilters };
}
